import React from 'react';
import {parseDocument} from 'utilities/DOM';
import domToReact from 'utilities/domToReact';
import CardHeader from '../shared/CardHeader';
import cx from 'classnames';
import styles from './Text.css?module';
import {selectNotebookCard} from 'state/card/cardSelectors';
import {loadNotebookCard} from 'state/card/cardActions';
import {connect} from 'react-redux';
import isEqual from 'lodash/isEqual';
import {
  notebookHasDynamicText,
  populateFunctionOverrideExpressions,
} from 'state/card/cardService';
import {selectIsFeatureEnabled} from 'state/feature/featureSelectors';
import SynergyWrapper from 'utilities/synergy/SynergyWrapper';
import {useRoosterCardTheme} from '../Rooster/useRoosterCardTheme';
import {setFontCSSVariablesBasedOnTheme, setFontCSSVariablesBasedOnViewStyle} from './TextUtils.ts';

export class TextContent extends React.Component {
  constructor(props) {
    super(...arguments);
    this.state = {
      synergyReady: false,
    };
    
    this.useNotebookStyles = props.viewStyle || (props.contextEntityType && props.contextEntityId);
    if (this.useNotebookStyles) {
      SynergyWrapper.loadSynergy().then(() => {
        const {ThemeUtils} = SynergyWrapper.synergy;
        this.themeUtils = new ThemeUtils();
        this.setState({
          synergyReady: true,
        });
      });
    }
  }

  componentDidMount() {
    this.loadNotebook();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.card.urn !== prevProps.card.urn ||
      !isEqual(this.props.filters, prevProps.filters) ||
      !isEqual(this.props.filterGroupIds, prevProps.filterGroupIds)
    ) {
      this.loadNotebook();
    }
  }

  hasSmartText() {
    const {card} = this.props;
    const hasDynamicText = notebookHasDynamicText(card);
    const hasDataSources = card.datasources.length > 0;
    return hasDataSources || hasDynamicText;
  }

  fontNotLoaded(fontName) {
    const doc = document.querySelectorAll(
      `link[href*="${fontName}"][href*="fonts.googleapis.com/css"]`
    );
    return (
      doc.length === 0 && (!doc.fonts || !doc.fonts.check(`12px '${fontName}'`))
    );
  }

  loadFonts() {
    if (!this.useNotebookStyles || !this.state.synergyReady) return;
    const fontFamilies = ['Sans', 'Slab', 'Condensed', 'Monospace', 'Serif'];
    const fonts = this.themeUtils.getFonts();
    const supportedFonts = Object.values(fonts).map(f => f.mapTo);

    const fontsNotLoaded = fontFamilies.filter(
      f =>
        fonts[f] &&
        supportedFonts.indexOf(fonts[f].mapTo) > -1 &&
        this.fontNotLoaded(fonts[f].mapTo)
    );
    if (fontsNotLoaded.length > 0) {
      const literalFontFamilies = fontsNotLoaded.map(f => fonts[f].mapTo);

      // get all the possible weights for these font families
      const weights = fontsNotLoaded.reduce((fontWeights, f) => {
        return {
          ...fontWeights,
          ...Object.values(fonts[f].weights).reduce(
            (acc, weight) => ({
              ...acc,
              [weight.value]: true,
            }),
            {}
          ),
        };
      }, {});
      const italicWeightsPart = Object.keys(weights)
        .map(w => `${w}italic`)
        .join(',');
      const weightsPart = Object.keys(weights).join(',');
      const fontFamiliesPart = literalFontFamilies
        .join('|')
        .replace(/\s/g, '+');
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `//fonts.googleapis.com/css?family=${fontFamiliesPart}:${italicWeightsPart},${weightsPart}`;
      document.head.appendChild(link);
      return new Promise((resolve, reject) => {
        link.onload = () => {
          resolve();
        };
        link.onerror = () => {
          reject();
        };
      });
    } else {
      return Promise.resolve();
    }
  }

  loadStyles() {
    const viewStyle = this.props.viewStyle;
    const theme = this.props.theme;
    const overriddenStyleId = this.props.overriddenStyleId;
    if (theme && this.state.synergyReady) {
      setFontCSSVariablesBasedOnTheme(theme, overriddenStyleId, this.themeUtils);
      
    }
    if (viewStyle && this.state.synergyReady) {
      setFontCSSVariablesBasedOnViewStyle(viewStyle, this.themeUtils);
    }
  }

  loadNotebook() {
    const {
      card,
      filters,
      filterGroupIds,
      segments,
      dateRangeFilter,
      dateTimeElement,
      overrideDateRange,
      functionOverrides,
      dataControlFilters,
    } = this.props;
    if (this.hasSmartText()) {
      this.props.loadNotebookCard(card.urn, {
        filters,
        segments,
        dateRangeFilter,
        dateTimeElement,
        overrideDateRange,
        dataControlContext: {filterGroupIds, filters: dataControlFilters},
        functionOverrides:
          functionOverrides &&
          populateFunctionOverrideExpressions(functionOverrides),
      });
    }
  }

  getTextHtml() {
    const {card, notebook} = this.props;
    const notebookHtml = (notebook && notebook.textHtml) || '';
    const metadataHtml = card.metadata.textHtml || '';
    return this.hasSmartText() ? notebookHtml : metadataHtml;
  }

  render() {
    const {
      textColor,
      debugDavinciNotebookEnabled,
      filters,
    } = this.props;
    this.loadFonts();
    this.loadStyles();
    const doc = parseDocument(
      `<div ${styles.htmlWrapper &&
        `class=${styles.htmlWrapper}`}>${this.getTextHtml()}</div>`
    );
    const node = doc.body.firstChild;
    Array.from(doc.body.getElementsByTagName('img')).forEach(
      img => (img.referrerPolicy = 'no-referrer')
    );
    if (this.useNotebookStyles && !this.state.synergyReady) return;
    return (
      <div
        className={cx(styles.content, {
          [styles.notebookThemeStyles]: this.useNotebookStyles,
        })}
        style={{color: textColor}}
      >
        <>
          {debugDavinciNotebookEnabled && (
            <p style={{overflowWrap: 'break-word'}}>
              {JSON.stringify(filters)}
            </p>
          )}
        </>
        <>{domToReact(node)}</>
      </div>
    );
  }
}

const ConnectedTextContent = connect(
  (state, {card}) => ({
    notebook: selectNotebookCard(state, card.urn),
    debugDavinciNotebookEnabled: selectIsFeatureEnabled(
      state,
      'debug-davinci-notebook'
    ),
  }),
  {
    loadNotebookCard,
  }
)(TextContent);

export const TextDetails = ({
  card,
  textColor,
  hasBackground,
  filters,
  segments,
  dateRangeFilter,
  dateTimeElement,
  overrideDateRange,
  filterGroupIds,
  functionOverrides,
  dataControlFilters,
  viewStyle,
  ...restProps
}) => {
  const theme = useRoosterCardTheme(restProps.contextEntityType, restProps.contextEntityId);
  return <div
    className={cx(styles.text, {
      [styles.hasBackground]: hasBackground,
    })}
  >
    <ConnectedTextContent
      card={card}
      textColor={textColor}
      filters={filters}
      segments={segments}
      dateRangeFilter={dateRangeFilter}
      dateTimeElement={dateTimeElement}
      overrideDateRange={overrideDateRange}
      dataControlFilters={dataControlFilters}
      filterGroupIds={filterGroupIds}
      functionOverrides={functionOverrides}
      viewStyle={viewStyle}
      theme={theme}
      {...restProps}
    />
  </div>
};

export const TextCard = ({
  card,
  filters,
  segments,
  dateRangeFilter,
  dateTimeElement,
  overrideDateRange,
  filterGroupIds,
  functionOverrides,
  dataControlFilters,
  viewStyle,
  ...restProps
}) => {
  const theme = useRoosterCardTheme(restProps.contextEntityType, restProps.contextEntityId);
  return <div className={cx(styles.text, styles.card)}>
    <CardHeader card={card} />
    <ConnectedTextContent
      card={card}
      filters={filters}
      segments={segments}
      dateRangeFilter={dateRangeFilter}
      dateTimeElement={dateTimeElement}
      overrideDateRange={overrideDateRange}
      dataControlFilters={dataControlFilters}
      filterGroupIds={filterGroupIds}
      functionOverrides={functionOverrides}
      viewStyle={viewStyle}
      theme={theme}
      {...restProps}
    />
  </div>
};
